import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { toast } from "@/components/ui/Toast";
import { useTranslation } from "@/hooks/useTranslation";
import type { AssessmentStatus } from "@/types";

// ─────────────────────────────────────────────
// VersioningTab
//
// Tab 1 of AssessmentFill. Shows entity metadata fields.
// Editable in DRAFT, read-only otherwise.
// Design: §6.3 Tab 1 — Versioning
// PRD: §6.3 Versioning fields
// ─────────────────────────────────────────────

export interface EntityMetadata {
  businessSponsor?: string;
  businessSponsorEntity?: string;
  itmProjectManager?: string;
  itmPmEntity?: string;
  cisoName?: string;
  department?: string;
  lastArchitectureReview?: string;
  initialProductionDate?: string;
  businessCriticality?: "LOW" | "MEDIUM" | "HIGH" | "NA";
  dataClassification?: "PUBLIC" | "INTERNAL" | "CONFIDENTIAL" | "RESTRICTED" | "NA";
  description?: string;
}

export interface VersioningTabProps {
  assessmentId: string;
  entityId: string;
  status: AssessmentStatus;
  metadata: EntityMetadata;
  createdAt: string;
  updatedAt: string;
  version: number;
  documentStatus: AssessmentStatus;
  createdBy?: string;
  approvedBy?: string;
  /** S-113: notifies the parent when there are unsaved metadata edits, so it
   *  can guard tab switches / navigation (Radix unmounts this tab on switch,
   *  silently destroying the un-saved form otherwise). */
  onDirtyChange?: (dirty: boolean) => void;
}

const CRITICALITY_OPTIONS = ["LOW", "MEDIUM", "HIGH", "NA"] as const;
const DATA_CLASS_OPTIONS = ["PUBLIC", "INTERNAL", "CONFIDENTIAL", "RESTRICTED", "NA"] as const;

// Key paths only (not user-visible text) — safe to keep at module scope,
// reuses the existing status.* translation keys.
const STATUS_LABEL_KEYS: Record<AssessmentStatus, string> = {
  DRAFT: "status.draft",
  SUBMITTED: "status.submitted",
  IN_REVIEW: "status.inReview",
  APPROVED: "status.approved",
  REJECTED: "status.rejected",
};

export function VersioningTab({
  assessmentId,
  entityId: _entityId,
  status,
  metadata,
  createdAt,
  updatedAt,
  version,
  createdBy,
  approvedBy,
  onDirtyChange,
}: VersioningTabProps) {
  const { t, language } = useTranslation();
  const queryClient = useQueryClient();
  const isEditable = status === "DRAFT";
  const [form, setForm] = useState<EntityMetadata>({ ...metadata });
  const [dirty, setDirty] = useState(false);

  // S-113: warn on refresh/close while there are unsaved edits. Tab-switch and
  // in-app navigation guards live in the parent (via onDirtyChange), because
  // Radix unmounts this component before it could intercept anything itself.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const mutation = useMutation({
    mutationFn: (meta: EntityMetadata) =>
      api.patch(`/assessments/${assessmentId}`, { metadata: meta }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", assessmentId] });
      toast.success(t("assessment.metadataSaved"));
      setDirty(false);
      onDirtyChange?.(false);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("assessment.metadataSaveFailed"));
    },
  });

  function handleChange(field: keyof EntityMetadata, value: string) {
    setForm((prev) => ({ ...prev, [field]: value || undefined }));
    setDirty(true);
    onDirtyChange?.(true);
  }

  function handleSave() {
    mutation.mutate(form);
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Auto fields */}
      <div className="rounded-card border border-border-default bg-card p-5">
        <h3 className="mb-4 text-[16px] font-semibold text-text-primary">{t("assessment.documentInfo")}</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-3">
          <ReadField label={t("assessment.formCreated")} value={formatDate(createdAt, language)} />
          <ReadField label={t("assessment.lastUpdated")} value={formatDate(updatedAt, language)} />
          <ReadField label={t("assessment.version")} value={`v${version}`} />
          <ReadField
            label={t("assessment.documentStatus")}
            value={<StatusBadge type="assessment" status={status} />}
          />
          {createdBy && <ReadField label={t("assessment.filledBy")} value={createdBy} />}
          {approvedBy && <ReadField label={t("assessment.approvedByLabel")} value={approvedBy} />}
        </div>
      </div>

      {/* Editable metadata */}
      <div className="rounded-card border border-border-default bg-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[16px] font-semibold text-text-primary">{t("assessment.applicationMetadata")}</h3>
          {isEditable && dirty && (
            <Button
              size="sm"
              onClick={handleSave}
              isLoading={mutation.isPending}
            >
              {t("common.save")}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label={t("assessment.businessSponsor")}
            value={form.businessSponsor ?? ""}
            onChange={(v) => handleChange("businessSponsor", v)}
            readOnly={!isEditable}
          />
          <Field
            label={t("assessment.businessSponsorEntity")}
            value={form.businessSponsorEntity ?? ""}
            onChange={(v) => handleChange("businessSponsorEntity", v)}
            readOnly={!isEditable}
          />
          <Field
            label={t("assessment.itmProjectManager")}
            value={form.itmProjectManager ?? ""}
            onChange={(v) => handleChange("itmProjectManager", v)}
            readOnly={!isEditable}
          />
          <Field
            label={t("assessment.itmPmEntity")}
            value={form.itmPmEntity ?? ""}
            onChange={(v) => handleChange("itmPmEntity", v)}
            readOnly={!isEditable}
          />
          <Field
            label={t("assessment.cisoName")}
            value={form.cisoName ?? ""}
            onChange={(v) => handleChange("cisoName", v)}
            readOnly={!isEditable}
          />
          <Field
            label={t("assessment.departmentUnit")}
            value={form.department ?? ""}
            onChange={(v) => handleChange("department", v)}
            readOnly={!isEditable}
          />
          <Field
            label={t("assessment.lastArchitectureReview")}
            type="date"
            value={form.lastArchitectureReview ?? ""}
            onChange={(v) => handleChange("lastArchitectureReview", v)}
            readOnly={!isEditable}
          />
          <Field
            label={t("assessment.initialProductionDate")}
            type="date"
            value={form.initialProductionDate ?? ""}
            onChange={(v) => handleChange("initialProductionDate", v)}
            readOnly={!isEditable}
          />

          {/* Business Criticality */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("assessment.businessCriticality")}
            </label>
            {isEditable ? (
              <select
                value={form.businessCriticality ?? ""}
                onChange={(e) => handleChange("businessCriticality", e.target.value)}
                className="h-9 w-full rounded-control border border-border-default bg-bg-input px-3 text-[14px] text-text-primary focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent"
              >
                <option value="">{t("common.selectPlaceholder")}</option>
                {CRITICALITY_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o === "NA" ? t("common.notApplicable") : t(`risk.${o.toLowerCase()}`)}
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex h-9 items-center">
                {form.businessCriticality && form.businessCriticality !== "NA" ? (
                  <StatusBadge type="criticality" status={form.businessCriticality} />
                ) : (
                  <span className="text-[14px] text-text-muted">-</span>
                )}
              </div>
            )}
          </div>

          {/* Data Classification */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("assessment.dataClassification")}
            </label>
            {isEditable ? (
              <select
                value={form.dataClassification ?? ""}
                onChange={(e) => handleChange("dataClassification", e.target.value)}
                className="h-9 w-full rounded-control border border-border-default bg-bg-input px-3 text-[14px] text-text-primary focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent"
              >
                <option value="">{t("common.selectPlaceholder")}</option>
                {DATA_CLASS_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o === "NA" ? t("common.notApplicable") : t(`entity.dataClassificationOptions.${o}`)}
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex h-9 items-center">
                {form.dataClassification ? (
                  <span
                    className="inline-flex items-center rounded-badge px-2 py-0.5 text-[12px] font-semibold"
                    style={{
                      backgroundColor: "var(--accent-bg)",
                      color: "var(--accent-text)",
                    }}
                  >
                    {form.dataClassification === "NA"
                      ? t("common.notApplicable")
                      : t(`entity.dataClassificationOptions.${form.dataClassification}`)}
                  </span>
                ) : (
                  <span className="text-[14px] text-text-muted">-</span>
                )}
              </div>
            )}
          </div>
        </div>

        {!isEditable && (
          <p className="mt-4 text-[13px] text-text-muted">
            {t("assessment.metadataReadOnlyForStatus")} {t(STATUS_LABEL_KEYS[status])}.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Internal helpers ──────────────────────────

function ReadField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[12px] font-medium uppercase tracking-wide text-text-muted">{label}</span>
      <span className="text-[14px] text-text-secondary">{value}</span>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  readOnly,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
  type?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[14px] font-medium text-text-secondary">{label}</label>
      {readOnly ? (
        <div className="flex h-9 items-center">
          <span className="text-[14px] text-text-secondary">{value || "-"}</span>
        </div>
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-full rounded-control border border-border-default bg-bg-input px-3 text-[14px] text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent"
        />
      )}
    </div>
  );
}
